// Login a firma del wallet, lato client.
//
// La skin la compra il wallet, non "chi conosce l'indirizzo". Per dimostrare di
// possedere il wallet firmiamo con Phantom un messaggio (firma off-chain: gratis,
// nessuna transazione). Il server verifica la firma ed emette un token; da lì in
// poi lo mandiamo su ogni azione che tocca inventario o saldo.

import bs58 from 'bs58';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Token in memoria per la sessione corrente, indicizzati per wallet.
const tokens = new Map();

const storageKey = (wallet) => `vexel_token:${wallet}`;

export function getToken(wallet) {
  if (!wallet) return null;
  if (tokens.has(wallet)) return tokens.get(wallet);
  try {
    const t = sessionStorage.getItem(storageKey(wallet));
    if (t) tokens.set(wallet, t);
    return t;
  } catch (e) {
    return null;
  }
}

function setToken(wallet, token) {
  tokens.set(wallet, token);
  try { sessionStorage.setItem(storageKey(wallet), token); } catch (e) {}
}

// Un token emesso altrove. Serve alla consegna della sessione fra due browser
// (lib/consegnaSessione): il token nasce nel browser di Phantom, dove la firma
// riesce, e arriva qui gia' fatto. Da questo punto in poi e' indistinguibile da
// uno ottenuto firmando in loco.
export function salvaToken(wallet, token) {
  setToken(wallet, token);
}

export function clearToken(wallet) {
  tokens.delete(wallet);
  try { sessionStorage.removeItem(storageKey(wallet)); } catch (e) {}
}

// Le tre tappe dell'handshake, separate.
//
// Sul desktop si susseguono dentro `login()` e nessuno le vede. Da telefono
// invece, con i deeplink, tra la sfida e la verifica il browser lascia la
// pagina e ci torna: le tappe devono poter essere chiamate da sole, in momenti
// diversi e da due caricamenti di pagina distinti. Il nonce vive cinque minuti
// sul server, quindi il giro attraverso l'app Phantom ci sta comodamente.

export async function richiediSfida(wallet) {
  if (!wallet) throw new Error('Wallet mancante');
  const res = await fetch(`${API_BASE}/api/auth/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wallet }),
  });
  if (!res.ok) throw new Error('Impossibile ottenere la sfida di login');
  const { message } = await res.json();
  return message;
}

// La firma arriva gia' in base58 dal deeplink di Phantom; dall'estensione
// arriva come byte grezzi. Accetta entrambe le forme.
export async function verificaFirma(wallet, firma) {
  const signature = typeof firma === 'string' ? firma : bs58.encode(firma);
  const res = await fetch(`${API_BASE}/api/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wallet, signature }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Firma non verificata');
  setToken(wallet, data.token);
  return data.token;
}

// Esegue l'handshake sfida → firma → verifica. Ritorna il token, o lancia.
// È il percorso dell'estensione, dove la firma si ottiene senza lasciare la
// pagina e le tre tappe stanno in una sola funzione.
export async function login(wallet, signMessage) {
  if (!wallet) throw new Error('Wallet mancante');
  if (typeof signMessage !== 'function') {
    throw new Error('Questo wallet non supporta la firma di messaggi');
  }
  const message = await richiediSfida(wallet);
  const signature = await signMessage(new TextEncoder().encode(message));
  return verificaFirma(wallet, signature);
}

// Login per gli pseudo-wallet "dev-..." usati dal pulsante DEV (nessuna firma).
export async function devLogin(wallet) {
  const res = await fetch(`${API_BASE}/api/auth/dev-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wallet }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Dev login fallito');
  setToken(wallet, data.token);
  return data.token;
}

// Intestazione da mettere sulle richieste protette.
export function authHeader(wallet) {
  const t = getToken(wallet);
  return t ? { Authorization: `Bearer ${t}` } : {};
}
